import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Divider,
  Button,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Language,
  Palette,
  Notifications,
  Security,
  Info
} from '@mui/icons-material';
import { useApp } from '../contexts/AppContext';

const Settings: React.FC = () => {
  const { state, setSettings, isAdmin } = useApp();
  const [showSaveAlert, setShowSaveAlert] = useState(false);

  // 설정 변경 핸들러
  const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const language = event.target.value as 'ko' | 'en';
    setSettings({ language });
    setShowSaveAlert(true);
  };

  const handleThemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const theme = event.target.value as 'light' | 'dark';
    setSettings({ theme });
    setShowSaveAlert(true);
  };

  return (
    <Box>
      {/* 헤더 */}
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        설정
      </Typography>
      <Typography variant="body1" color="textSecondary" mb={3}>
        애플리케이션 환경설정을 관리합니다.
      </Typography>

      {/* 언어 설정 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Language color="primary" />
            <Typography variant="h6">언어 설정</Typography>
          </Box>
          
          <FormControl component="fieldset">
            <FormLabel component="legend">표시 언어</FormLabel>
            <RadioGroup
              value={state.settings.language}
              onChange={handleLanguageChange}
              sx={{ mt: 1 }}
            >
              <FormControlLabel 
                value="ko" 
                control={<Radio />} 
                label="한국어" 
              />
              <FormControlLabel 
                value="en" 
                control={<Radio />} 
                label="English" 
                disabled 
              />
            </RadioGroup>
          </FormControl>
          
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            * 영어 지원은 향후 업데이트 예정입니다.
          </Typography>
        </CardContent>
      </Card>

      {/* 테마 설정 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Palette color="primary" />
            <Typography variant="h6">테마 설정</Typography>
          </Box>
          
          <FormControl component="fieldset">
            <FormLabel component="legend">화면 테마</FormLabel>
            <RadioGroup
              value={state.settings.theme}
              onChange={handleThemeChange}
              sx={{ mt: 1 }}
            >
              <FormControlLabel 
                value="light" 
                control={<Radio />} 
                label="라이트 모드" 
              />
              <FormControlLabel 
                value="dark" 
                control={<Radio />} 
                label="다크 모드" 
                disabled 
              />
            </RadioGroup>
          </FormControl>
          
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            * 다크 모드는 향후 업데이트 예정입니다.
          </Typography>
        </CardContent>
      </Card>

      {/* 계정 정보 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Info color="primary" />
            <Typography variant="h6">계정 정보</Typography>
          </Box>
          
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>이메일:</strong> {state.user?.email}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>이름:</strong> {state.user?.displayName || '미설정'}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>권한:</strong> {isAdmin ? '관리자' : '일반 사용자'}
            </Typography>
            {state.permissions?.canEdit && !isAdmin && (
              <Typography variant="body2" color="primary">
                편집 권한이 부여되었습니다.
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* 관리자 전용 섹션 */}
      {isAdmin && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Security color="primary" />
              <Typography variant="h6">관리자 설정</Typography>
            </Box>
            
            <Typography variant="body2" color="textSecondary" paragraph>
              관리자로서 다음 기능들을 사용할 수 있습니다:
            </Typography>
            
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" gutterBottom>
                • 모든 인원 정보 편집 및 삭제
              </Typography>
              <Typography variant="body2" gutterBottom>
                • 사용자 권한 관리
              </Typography>
              <Typography variant="body2" gutterBottom>
                • 시스템 설정 변경
              </Typography>
              <Typography variant="body2" gutterBottom>
                • 데이터 내보내기 및 백업
              </Typography>
            </Box>
            
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => {
                // 권한 관리 페이지로 이동
                window.location.href = '/permissions';
              }}
            >
              권한 관리로 이동
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 앱 정보 */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            애플리케이션 정보
          </Typography>
          
          <Divider sx={{ my: 2 }} />
          
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>버전:</strong> 1.0.0
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>개발:</strong> Organization Manager Team
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>기술 스택:</strong> React, Firebase, Material-UI
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>업데이트:</strong> 2025.08.25
            </Typography>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary">
              🔒 안전한 Firebase 인증으로 보호됩니다<br />
              📱 PWA 기술로 모든 기기에서 이용 가능<br />
              ⚡ 실시간 데이터 동기화 지원
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* 저장 성공 알림 */}
      <Snackbar
        open={showSaveAlert}
        autoHideDuration={3000}
        onClose={() => setShowSaveAlert(false)}
      >
        <Alert 
          onClose={() => setShowSaveAlert(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          설정이 저장되었습니다.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
